/**
 * Author: Claude Code
 * Version: v3.0.56
 * Modified: 2025-09-06 Beijing Time
 * 
 * Task 1.2.1: 统一的状态同步协议类型定义
 * 实现Backend GameEngine与Frontend Scene的直接状态同步
 */

// ==================== 基础类型定义 ====================

export interface Position {
  x: number;
  y: number;
}

export interface Health {
  current: number;
  max: number;
}

export interface GameContext {
  gameId: string;
  turnNumber: number;
  phase: 'INITIALIZATION' | 'AGENT_ACTIONS' | 'COMBAT_RESOLUTION' | 'ENVIRONMENT_UPDATE' | 'TURN_END';
  gameTime: number;
  remainingAgents: number;
  safeZoneRadius: number;
}

// ==================== 状态更新消息类型 ====================

export type StateUpdateType = 
  | 'GAME_STATE_UPDATE'     // 全量游戏状态更新
  | 'AGENT_STATE_UPDATE'    // 单个Agent状态增量更新  
  | 'RESOURCE_STATE_UPDATE' // 资源状态更新
  | 'SAFEZONE_STATE_UPDATE' // 安全区状态更新
  | 'COMBAT_STATE_UPDATE';  // 战斗状态更新

// ==================== 统一状态更新接口 ====================

export interface BaseStateUpdate {
  type: StateUpdateType;
  timestamp: string;      // ISO时间戳
  sequence: number;       // 序列号用于排序
  gameId?: string;        // 游戏ID (可选，用于多游戏场景)
}

// ==================== 具体状态更新类型 ====================

export interface GameStateUpdate extends BaseStateUpdate {
  type: 'GAME_STATE_UPDATE';
  data: {
    context: GameContext;
    agents: AgentState[];
    resources: ResourceState[];
    safeZone: SafeZoneState;
    combat?: CombatState;
  };
}

export interface AgentStateUpdate extends BaseStateUpdate {
  type: 'AGENT_STATE_UPDATE';
  data: {
    agentId: string;
    position?: Position;
    health?: Health;
    status?: 'active' | 'dead' | 'eliminated';
    inventory?: string[];    // 携带的资源IDs
    lastAction?: string;     // 最后执行的动作
    llmThinking?: boolean;   // 是否在LLM思考中
  };
}

export interface ResourceStateUpdate extends BaseStateUpdate {
  type: 'RESOURCE_STATE_UPDATE';
  data: {
    resourceId: string;
    resourceType: 'Axe' | 'Lance' | 'Sword' | 'Large Medkit' | 'Small Medkit';
    position?: Position;
    status: 'spawned' | 'collected' | 'disappeared';
    collectedBy?: string;    // 被哪个agent收集
    spawnTime?: string;      // 生成时间
  };
}

export interface SafeZoneStateUpdate extends BaseStateUpdate {
  type: 'SAFEZONE_STATE_UPDATE';
  data: {
    centerX: number;
    centerY: number;
    currentRadius: number;
    targetRadius: number;
    contractionTime: number;  // 收缩持续时间(ms)
    isContracting: boolean;
    warningTime?: number;     // 警告时间(ms)
  };
}

export interface CombatStateUpdate extends BaseStateUpdate {
  type: 'COMBAT_STATE_UPDATE';
  data: {
    combatId: string;
    attackerId: string;
    targetId: string;
    weapon?: string;
    damage: number;
    result: 'hit' | 'miss' | 'critical' | 'kill';
    attackerHealth: Health;
    targetHealth: Health;
  };
}

// ==================== 联合类型 ====================

export type AnyStateUpdate = 
  | GameStateUpdate 
  | AgentStateUpdate 
  | ResourceStateUpdate 
  | SafeZoneStateUpdate 
  | CombatStateUpdate;

// ==================== 状态实体定义 ====================

export interface AgentState {
  agentId: string;
  position: Position;
  health: Health;
  status: 'active' | 'dead' | 'eliminated';
  inventory: string[];
  lastAction?: string;
  llmThinking: boolean;
}

export interface ResourceState {
  resourceId: string;
  resourceType: 'Axe' | 'Lance' | 'Sword' | 'Large Medkit' | 'Small Medkit';
  position: Position;
  status: 'spawned' | 'collected' | 'disappeared';
  collectedBy?: string;
  spawnTime: string;
}

export interface SafeZoneState {
  centerX: number;
  centerY: number;
  currentRadius: number;
  targetRadius: number;
  contractionTime: number;
  isContracting: boolean;
  warningTime?: number;
}

export interface CombatState {
  combatId: string;
  attackerId: string;
  targetId: string;
  weapon?: string;
  damage: number;
  result: 'hit' | 'miss' | 'critical' | 'kill';
  timestamp: string;
}

// ==================== 状态验证接口 ====================

export interface StateUpdateValidator {
  validateStateUpdate(update: any): boolean;
  validateSequence(sequence: number): boolean;
  validateTimestamp(timestamp: string): boolean;
}

// ==================== 状态同步配置 ====================

export const STATE_SYNC_CONFIG = {
  MAX_SEQUENCE_GAP: 10,           // 最大序列号间隔
  UPDATE_TIMEOUT: 5000,           // 状态更新超时时间(ms)
  RETRY_ATTEMPTS: 3,              // 重试次数
  BATCH_SIZE: 50,                 // 批量处理大小
  SEQUENCE_BUFFER_SIZE: 100       // 序列号缓冲区大小
} as const;